// -------------------------------------------------------------------------------------------------------------------
// Keep everything in this file in sync with schema.go.
// -------------------------------------------------------------------------------------------------------------------

import { DocumentCriterion, SearchCriteria, TimeRange, PlatformEnum } from '@fullstory/client-api';
import { S } from '@fullstory/util/sexpr';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { int64 } from '@fullstory/proto/google/protobuf';

export const AnyPlatform: PlatformEnum = 'Any' as PlatformEnum;

// Sets a dependent filter for all behaviors requiring them to happen on one of the supplied PageClusterId.
// If no behaviors exist, will add a single behavior with just a dependent set.
export function setClusterIds(segmentDefinition: SegmentDefinition, clusterIds: number[]) {
  if (!segmentDefinition.behaviors?.length) {
    segmentDefinition.behaviors = [{ event: { primary: null, deps: [] } }];
  }

  const clusterIdStrs = clusterIds.map(id => int64.fromNumber(id));

  for (let i = 0; i < segmentDefinition.behaviors.length; i++) {
    const beh = segmentDefinition.behaviors[i];
    if (beh?.event) {
      beh.event.deps.push({ fsPageClusterId: { equals: { value: clusterIdStrs } } });
    }
  }
}

// Sets a dependent filter for all behaviors requiring them to happen on the supplied Page.
// If no behaviors exist, will add a single behavior with just a dependent set.
export function setPageDef(segmentDefinition: SegmentDefinition, pageDefId: number) {
  if (!segmentDefinition.behaviors?.length) {
    segmentDefinition.behaviors = [{ event: { primary: null, deps: [] } }];
  }
  const pageDefIds = [int64.fromNumber(pageDefId)];
  for (let i = 0; i < segmentDefinition.behaviors.length; i++) {
    const beh = segmentDefinition.behaviors[i];
    if (beh?.event) {
      beh.event.deps.push({ pageDefId: { equals: { value: pageDefIds, spec: [] } } });
    }
  }
}

export function isAllTime(segmentDefinition: SegmentDefinition | null | undefined): boolean {
  const timeRange = segmentDefinition?.constraints?.timeRange;
  if (!timeRange) {
    return true;
  }
  return timeRange.startTime == '' && timeRange.endTime == '';
}

export function DecodeFromSExpr(encodedQuery: S): SearchCriteria | undefined {
  // Bad encoding of search query. Has too few components
  if (!encodedQuery.kids || encodedQuery.kids.length < 5) {
    return undefined;
  }

  const queryParts = encodedQuery.kids;
  let timeRange: TimeRange = { Start: '', End: '' };
  const timeRangeS = queryParts[0];
  if (timeRangeS?.kids) {
    // Bad encoding of TimeRange in search query
    if (timeRangeS.kids.length != 2) {
      return undefined;
    }
    timeRange = <TimeRange>{
      Start: decodeURIComponent(timeRangeS.kids[0]?.value ?? ''),
      End: decodeURIComponent(timeRangeS.kids[1]?.value ?? ''),
    };
  }

  // Bad encoding of user criteria
  if (!queryParts[1]?.kids) {
    return undefined;
  }
  const userTipCriteria = _decodeXDocs(queryParts[1].kids);

  const eventCriteria: DocumentCriterion[][] = [];
  const ecS = queryParts[3];
  // Bad encoding of event criteria
  if (!ecS?.kids) {
    return undefined;
  }
  ecS.kids.forEach(ec => {
    eventCriteria.push(_decodeXDocs((<S>ec).kids ?? []));
  });

  const eventNeverCriteria: DocumentCriterion[][] = [];
  const encS = queryParts[4];
  // Bad encoding of event never criteria
  if (!encS?.kids) {
    return undefined;
  }
  encS.kids.forEach(enc => {
    eventNeverCriteria.push(_decodeXDocs((<S>enc).kids ?? []));
  });

  const sc = <SearchCriteria>{
    TimeRange: timeRange,
    UserTipCriteria: userTipCriteria,
    EventCriteria: eventCriteria,
    EventNeverCriteria: eventNeverCriteria,
  };

  // For now. Only populate if they are there. Rest of the system treats uninitialized as false.
  if (queryParts.length > 5) {
    const constraints = queryParts?.[5]?.kids;
    if (constraints) {
      sc.EnforceEventOrder = constraints?.[0]?.value != '';
      sc.InSameSession = constraints.length > 1 && constraints[1]?.value != '';
      if (constraints.length > 2 && constraints[2]?.value) {
        sc.Platform = decodeURIComponent(queryParts[5]?.kids![2]?.value!) as PlatformEnum;
      }
    }
  }

  return sc;
}

function _decodeXDocs(encoded: S[]): DocumentCriterion[] {
  const decoded: DocumentCriterion[] = [];
  encoded.forEach(encodedDoc => {
    // SExpr must have exactly three parts - field, op, value.  If not three parts, skip conversion
    // for that SExpr
    if (!encodedDoc.kids || encodedDoc.kids.length != 3) {
      return;
    }
    const docC = <DocumentCriterion>{
      Field: decodeURIComponent(encodedDoc.kids[0]?.value ?? ''),
      Op: decodeURIComponent(encodedDoc.kids[1]?.value ?? ''),
      Value: JSON.parse(
        decodeURIComponent(encodedDoc.kids[2]?.value ?? '')
          .replace(/\|_/g, '/')
          .replace(/\|,/g, '\\'),
      ),
    };
    decoded.push(docC);
  });
  return decoded;
}

// Encodes an SearchCriteria as an SExpression.
export function EncodeToSExpr(q: SearchCriteria): S {
  // Emit all the fields of the SearchCriteria to an SExpr.
  const timeRange = <S>{}; // TimeRange
  const userTipCriteria = <S>{ kids: [] }; // DocumentCriterion[];
  const accountTipCriteria_Deprecated = <S>{ kids: [] };
  const eventCriteria = <S>{ kids: [] }; // DocumentCriterion[][];
  const eventNeverCriteria = <S>{ kids: [] }; // DocumentCriterion[][];
  const eventConstraints = <S>{}; // enforce order, in-same-session

  // The one SExpr to rule them all.
  const encoded = <S>{
    kids: [
      timeRange,
      userTipCriteria,
      accountTipCriteria_Deprecated,
      eventCriteria,
      eventNeverCriteria,
      eventConstraints,
    ],
  };
  if (q == null) {
    return encoded;
  }

  if (q.TimeRange && q.TimeRange.Start && q.TimeRange.End) {
    timeRange.kids = [
      <S>{ value: encodeURIComponent(q.TimeRange.Start) },
      <S>{ value: encodeURIComponent(q.TimeRange.End) },
    ];
  }

  if (q.UserTipCriteria && q.UserTipCriteria.length > 0) {
    userTipCriteria.kids = _encodeXDocs(q.UserTipCriteria);
  }

  if (q.EventCriteria && q.EventCriteria.length > 0) {
    q.EventCriteria.forEach(ec => {
      if (ec) eventCriteria.kids!.push(<S>{ kids: _encodeXDocs(ec) });
    });
  }

  if (q.EventNeverCriteria && q.EventNeverCriteria.length > 0) {
    q.EventNeverCriteria.forEach(ec => {
      if (ec) eventNeverCriteria.kids!.push(<S>{ kids: _encodeXDocs(ec) });
    });
  }

  if (q.EnforceEventOrder || q.InSameSession || (q.Platform && q.Platform != AnyPlatform)) {
    // Because the platform value could contain whitespace, this won't parse nicely, so we use the name as the URL string.
    eventConstraints.kids = [
      <S>{ value: q.EnforceEventOrder ? 'ordered' : '' },
      <S>{ value: q.InSameSession ? 'samesession' : '' },
      <S>{ value: q.Platform && q.Platform != AnyPlatform ? encodeURIComponent(q.Platform) : '' },
    ];
  }

  return encoded;
}

function _encodeXDocs(docs: (DocumentCriterion | null)[]): S[] {
  const encoded: S[] = [];
  docs.forEach(docCriterion => {
    if (docCriterion) {
      encoded.push(<S>{
        kids: [
          <S>{ value: docCriterion.Field },
          <S>{ value: docCriterion.Op },
          // "encodeURIComponent escapes all characters except the following: alphabetic, decimal, digits, common emojis, - _ . ! ~ * ' ( )"
          <S>{
            value: JSON.stringify(docCriterion.Value).replace(
              /[^a-zA-Z0-9\uD83C-\uDBFF\uDC00-\uDFFF\-_!~*']/gu,
              c => {
                switch (c) {
                  case '\\':
                    return '|,';
                  case '/':
                    return '|_';
                  case '(':
                    return '%28';
                  case ')':
                    return '%29';
                  default:
                    return encodeURIComponent(c);
                }
              },
            ),
          },
        ],
      });
    }
  });
  return encoded;
}
